import { ReceiptText } from "lucide-react";
import { Link } from "react-router-dom";
import { DocShell, DocCard } from "../components/DocShell";

export default function Refund() {
  return (
    <DocShell
      pageTestId="refund-page"
      icon={ReceiptText}
      eyebrow="Giving with clarity"
      title="Refund Policy"
      subtitle="Why gifts on Haplap are final — and how we handle genuine disputes."
      updated="June 2026">
      <DocCard testId="refund-intro">
        <p>
          Haplap is a platform for giving. When you tip someone directly, contribute to the Community
          Tip Jar, or support a Neighbors in Need fund, you are making a donation to help a real
          person. Please give thoughtfully, because donations and tips are final.
        </p>
      </DocCard>

      <DocCard heading="Donations and tips are non-refundable" testId="refund-final">
        <p>
          All contributions on Haplap — direct tips, Community Tip Jar deposits, recurring pledges
          already charged, gift-card loads and group fund contributions — are{" "}
          <strong>final and non-refundable</strong>.
        </p>
        <p>
          This is core to our mission: <strong>100% of what you give goes to the individuals asking
          for help.</strong> Money is often withdrawn to a person's own wallet, shared out through
          the daily jar split, or paid out to a beneficiary very quickly. Once that help has reached
          the person who needed it, it has left the platform — and we have no way to get it back for
          you, and we cannot cover it ourselves.
        </p>
      </DocCard>

      <DocCard heading="Recurring pledges" testId="refund-recurring">
        <p>
          You can cancel a monthly pledge at any time to stop future charges. Cancelling stops the
          next payment — charges that have already been made and delivered to a recipient are final
          and non-refundable, like any other gift.
        </p>
      </DocCard>

      <DocCard heading="Genuine disputes" testId="refund-disputes">
        <p>
          We do care about fairness. If you believe a payment was made in error, was unauthorized, or
          involved fraud or abuse of the platform, please contact us within{" "}
          <strong>14 days</strong> of the transaction at{" "}
          <a className="text-copper underline" href="mailto:support@haplap.ca">support@haplap.ca</a>{" "}
          with the details. We will review each case individually.
        </p>
        <p>
          Where funds have not yet left the platform and a case is legitimate, we will do our best to
          make it right. Where funds have already been delivered to a recipient, we may be unable to
          issue a refund even after review — but we will always investigate suspected fraud and take
          action against bad actors.
        </p>
      </DocCard>

      <DocCard heading="Failed or duplicate payments" testId="refund-technical">
        <p>
          If a technical error caused a duplicate charge or a payment that never resulted in a
          completed gift, contact us and we will correct it. Our payment provider (Stripe) also
          handles certain authorization and processing reversals automatically.
        </p>
      </DocCard>

      <DocCard heading="How to reach us" testId="refund-contact">
        <p>
          For anything about a payment, email{" "}
          <a className="text-copper underline" href="mailto:support@haplap.ca">support@haplap.ca</a>.
          You can also read more in our <Link to="/faq" className="text-copper underline">FAQ</Link>{" "}
          and <Link to="/terms" className="text-copper underline">Terms of Service</Link>.
        </p>
      </DocCard>
    </DocShell>
  );
}
